import React, { useEffect, useMemo, useRef, useState } from "react";
import "./MeetingInsightsModal.css";

const EMPTY_SUMMARY = {
  overview: "",
  strengths: [],
  weaknesses: [],
  communication_style: "",
  technical_observations: [],
  recommendation: "",
};

const STATUS_TONES = {
  completed: {
    background: "#ecfdf5",
    borderColor: "#bbf7d0",
    color: "#047857",
  },
  processing: {
    background: "#eff6ff",
    borderColor: "#bfdbfe",
    color: "#1d4ed8",
  },
  pending: {
    background: "#fff7ed",
    borderColor: "#fed7aa",
    color: "#c2410c",
  },
  failed: {
    background: "#fff1f2",
    borderColor: "#fecdd3",
    color: "#be123c",
  },
  unavailable: {
    background: "#f8fafc",
    borderColor: "#e2e8f0",
    color: "#475569",
  },
};

function normalizeSummary(summary) {
  if (!summary || typeof summary !== "object") {
    return EMPTY_SUMMARY;
  }
  return {
    ...EMPTY_SUMMARY,
    ...summary,
    strengths: Array.isArray(summary.strengths) ? summary.strengths : [],
    weaknesses: Array.isArray(summary.weaknesses) ? summary.weaknesses : [],
    technical_observations: Array.isArray(summary.technical_observations)
      ? summary.technical_observations
      : [],
  };
}

function formatDateLabel(value) {
  if (!value) return "Not available";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatStatusLabel(value, fallback = "Pending") {
  if (!value) return fallback;
  return String(value)
    .split("_")
    .join(" ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

async function readJsonResponse(response) {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    return { error: text };
  }
}

async function requestInsights(url, options = {}) {
  const response = await fetch(url, options);
  const payload = await readJsonResponse(response);
  if (!response.ok) {
    throw new Error(payload.error || payload.details || `Request failed (${response.status})`);
  }
  return payload;
}

const StatusPill = ({ label, status }) => {
  const tone = STATUS_TONES[status] || STATUS_TONES.unavailable;
  return (
    <div
      className="meeting-insights-pill"
      style={{
        background: tone.background,
        borderColor: tone.borderColor,
        color: tone.color,
      }}
    >
      <span className="meeting-insights-pill-dot" />
      <span>{label}: {formatStatusLabel(status, "Unavailable")}</span>
    </div>
  );
};

const MeetingInsightsModal = ({ candidate, jobId, jobTitle, onClose }) => {
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({
    transcript: false,
    summary: false,
    chat: false,
    viewTranscript: false,
  });
  const [uiError, setUiError] = useState("");
  const [question, setQuestion] = useState("");
  const autoFetchAttemptedRef = useRef(false);

  const candidateEmail = candidate?.email || "";
  const candidateName = candidate?.name || "Candidate";

  const summary = useMemo(
    () => normalizeSummary(insights?.summary),
    [insights?.summary]
  );
  const transcriptSegments = Array.isArray(insights?.transcript_segments)
    ? insights.transcript_segments
    : [];
  const chatHistory = Array.isArray(insights?.chat_history) ? insights.chat_history : [];
  const booking = insights?.booking || null;
  const transcriptPdfUrl = insights?.transcript_pdf_url || "";
  const liveRoomId = insights?.live_room_id || booking?.live_room_id || "";

  const hasTranscript = transcriptSegments.length > 0;
  const canViewTranscript = Boolean(
    (hasTranscript || String(insights?.transcript_status || "").toLowerCase() === "completed") &&
      (liveRoomId || transcriptPdfUrl)
  );
  const hasSummary = Boolean(
    summary.overview ||
      summary.communication_style ||
      summary.recommendation ||
      summary.strengths.length ||
      summary.weaknesses.length ||
      summary.technical_observations.length
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    autoFetchAttemptedRef.current = false;
    if (!candidateEmail || !jobId) {
      setIsLoading(false);
      setUiError("Candidate email or job id is missing, so insights cannot be loaded.");
      return;
    }
    loadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateEmail, jobId]);

  const loadState = async () => {
    setIsLoading(true);
    setUiError("");
    try {
      const params = new URLSearchParams({
        candidate_email: candidateEmail,
        job_id: String(jobId),
      });
      const payload = await requestInsights(`/api/meeting/post-interview?${params.toString()}`);
      setInsights(payload);
    } catch (error) {
      setUiError(error.message || "Failed to load interview insights.");
    } finally {
      setIsLoading(false);
    }
  };

  const runAction = async (actionKey, url, body) => {
    setUiError("");
    setActionLoading((prev) => ({ ...prev, [actionKey]: true }));
    try {
      const payload = await requestInsights(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setInsights(payload);
      return payload;
    } catch (error) {
      setUiError(error.message || "Request failed.");
      return null;
    } finally {
      setActionLoading((prev) => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleFetchTranscript = async (forceRefresh = false) => {
    await runAction("transcript", "/api/meeting/post-interview/fetch-transcript", {
      candidate_email: candidateEmail,
      job_id: String(jobId),
      force_refresh: forceRefresh,
    });
  };

  useEffect(() => {
    if (isLoading || actionLoading.transcript || hasTranscript || !insights?.has_booking) {
      return;
    }
    if (autoFetchAttemptedRef.current) {
      return;
    }
    const provider = String(booking?.meeting_provider || "").trim().toLowerCase();
    const isLiveMeet = provider === "live_meet" || provider === "search_job_meet";
    if (isLiveMeet && !liveRoomId) {
      return;
    }
    if (!isLiveMeet && !insights?.meeting_completed && !insights?.transcript_dummy_mode) {
      return;
    }
    autoFetchAttemptedRef.current = true;
    handleFetchTranscript(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, actionLoading.transcript, hasTranscript, insights?.has_booking, liveRoomId, candidateEmail, jobId]);

  const handleViewTranscript = async () => {
    setUiError("");
    setActionLoading((prev) => ({ ...prev, viewTranscript: true }));
    try {
      if (liveRoomId) {
        const response = await fetch(
          `/api/live-interview/rooms/${encodeURIComponent(liveRoomId)}/transcript/pdf-url?role=interviewer`
        );
        const payload = await readJsonResponse(response);
        if (!response.ok || payload?.success === false) {
          throw new Error(
            payload?.message ||
              payload?.error ||
              `Transcript PDF request failed (${response.status})`
          );
        }
        if (!payload?.pdf_url) {
          throw new Error("Transcript PDF URL is not available.");
        }
        window.open(payload.pdf_url, "_blank", "noopener,noreferrer");
        return;
      }

      if (!transcriptPdfUrl) {
        throw new Error("Transcript PDF is not available yet.");
      }
      window.open(transcriptPdfUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      setUiError(error.message || "Failed to open transcript PDF.");
    } finally {
      setActionLoading((prev) => ({ ...prev, viewTranscript: false }));
    }
  };

  const handleGenerateSummary = async (forceRefresh = false) => {
    await runAction("summary", "/api/meeting/post-interview/generate-summary", {
      candidate_email: candidateEmail,
      job_id: String(jobId),
      force_refresh: forceRefresh,
    });
  };

  const handleAskQuestion = async () => {
    const trimmed = question.trim();
    if (!trimmed) {
      setUiError("Enter a question before sending it to AI chat.");
      return;
    }
    const payload = await runAction("chat", "/api/meeting/post-interview/chat", {
      candidate_email: candidateEmail,
      job_id: String(jobId),
      question: trimmed,
    });
    if (payload) {
      setQuestion("");
    }
  };

  const transcriptButtonLabel = hasTranscript ? "Refresh Transcript" : "Fetch Transcript";
  const summaryButtonLabel = hasSummary ? "Refresh Summary" : "Generate Summary";

  return (
    <div className="meeting-insights-overlay" onClick={onClose}>
      <div className="meeting-insights-modal" onClick={(event) => event.stopPropagation()}>
        <div className="meeting-insights-header">
          <div>
            <h2 className="meeting-insights-title">Interview Insights</h2>
            <p className="meeting-insights-subtitle">
              {candidateName} ({candidateEmail || "No email"}){jobTitle ? ` - ${jobTitle}` : ""}
            </p>
          </div>
          <button className="meeting-insights-close" onClick={onClose} aria-label="Close insights">
            X
          </button>
        </div>

        <div className="meeting-insights-scroll">
          <div className="meeting-insights-status-row">
            <StatusPill label="Booking" status={insights?.has_booking ? (insights?.meeting_completed ? "completed" : "pending") : "unavailable"} />
            <StatusPill label="Transcript" status={insights?.transcript_status || "pending"} />
            <StatusPill label="Summary" status={insights?.summary_status || "pending"} />
            {insights?.transcript_dummy_mode ? (
              <div className="meeting-insights-pill" style={STATUS_TONES.processing}>
                <span className="meeting-insights-pill-dot" />
                <span>Dummy transcript mode enabled</span>
              </div>
            ) : null}
          </div>

          <div className="meeting-insights-actions">
            <button
              className="meeting-insights-btn meeting-insights-btn-ghost"
              onClick={loadState}
              disabled={
                isLoading ||
                actionLoading.transcript ||
                actionLoading.summary ||
                actionLoading.chat ||
                actionLoading.viewTranscript
              }
            >
              {isLoading ? "Loading..." : "Reload State"}
            </button>
            <button
              className="meeting-insights-btn meeting-insights-btn-primary"
              onClick={() => handleFetchTranscript(hasTranscript)}
              disabled={!candidateEmail || !jobId || actionLoading.transcript}
            >
              {actionLoading.transcript ? "Working..." : transcriptButtonLabel}
            </button>
            <button
              className="meeting-insights-btn meeting-insights-btn-secondary"
              onClick={() => handleGenerateSummary(hasSummary)}
              disabled={!hasTranscript || actionLoading.summary}
            >
              {actionLoading.summary ? "Working..." : summaryButtonLabel}
            </button>
            {canViewTranscript ? (
              <button
                className="meeting-insights-btn meeting-insights-btn-secondary"
                onClick={handleViewTranscript}
                disabled={actionLoading.viewTranscript}
              >
                {actionLoading.viewTranscript ? "Opening..." : "View Transcript"}
              </button>
            ) : null}
          </div>

          {uiError ? <div className="meeting-insights-banner">{uiError}</div> : null}
          {!uiError && insights?.last_error ? (
            <div className="meeting-insights-banner">{insights.last_error}</div>
          ) : null}

          {isLoading ? (
            <div className="meeting-insights-empty">Loading interview insights...</div>
          ) : (
            <div className="meeting-insights-grid">
              <div className="meeting-insights-card meeting-insights-card-wide">
                <h3>Interview Booking</h3>
                {insights?.has_booking ? (
                  <div className="meeting-insights-info-grid">
                    <div className="meeting-insights-info-item">
                      <span className="meeting-insights-info-label">Candidate</span>
                      <div className="meeting-insights-info-value">
                        {booking?.candidate_name || candidateName}
                      </div>
                    </div>
                    <div className="meeting-insights-info-item">
                      <span className="meeting-insights-info-label">Interviewer</span>
                      <div className="meeting-insights-info-value">
                        {booking?.interviewer_name || "Not assigned"}
                      </div>
                    </div>
                    <div className="meeting-insights-info-item">
                      <span className="meeting-insights-info-label">Interview Slot</span>
                      <div className="meeting-insights-info-value">
                        {formatDateLabel(booking?.slot_value || booking?.slot_label)}
                      </div>
                    </div>
                    <div className="meeting-insights-info-item">
                      <span className="meeting-insights-info-label">Completed</span>
                      <div className="meeting-insights-info-value">
                        {insights?.meeting_completed ? "Yes" : "Not yet"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="meeting-insights-empty">
                    No confirmed interview booking was found for this candidate and job yet.
                  </div>
                )}
              </div>

              <div className="meeting-insights-card meeting-insights-card-side">
                <h3>Summary Snapshot</h3>
                {hasSummary ? (
                  <div className="meeting-insights-summary-stack">
                    <div className="meeting-insights-summary-block">
                      <h4>Overview</h4>
                      <p>{summary.overview || "No overview generated yet."}</p>
                    </div>
                    <div className="meeting-insights-summary-block">
                      <h4>Communication Style</h4>
                      <p>{summary.communication_style || "No communication note generated yet."}</p>
                    </div>
                    <div className="meeting-insights-summary-block">
                      <h4>Recommendation</h4>
                      <p>{summary.recommendation || "No recommendation generated yet."}</p>
                    </div>
                  </div>
                ) : (
                  <div className="meeting-insights-empty">
                    Generate a summary after the transcript is available to see the interview overview here.
                  </div>
                )}
              </div>

              <div className="meeting-insights-card meeting-insights-card-side">
                <h3>Strengths</h3>
                {summary.strengths.length ? (
                  <ul className="meeting-insights-list">
                    {summary.strengths.map((item, index) => (
                      <li key={`strength-${index}`}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="meeting-insights-empty">No strengths generated yet.</div>
                )}
              </div>

              <div className="meeting-insights-card meeting-insights-card-wide">
                <h3>Weaknesses and Technical Notes</h3>
                <div className="meeting-insights-grid" style={{ gap: 12 }}>
                  <div className="meeting-insights-summary-block" style={{ gridColumn: "span 6" }}>
                    <h4>Weaknesses</h4>
                    {summary.weaknesses.length ? (
                      <ul className="meeting-insights-list">
                        {summary.weaknesses.map((item, index) => (
                          <li key={`weakness-${index}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="meeting-insights-muted">No weaknesses generated yet.</p>
                    )}
                  </div>
                  <div className="meeting-insights-summary-block" style={{ gridColumn: "span 6" }}>
                    <h4>Technical Observations</h4>
                    {summary.technical_observations.length ? (
                      <ul className="meeting-insights-list">
                        {summary.technical_observations.map((item, index) => (
                          <li key={`technical-${index}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="meeting-insights-muted">No technical observations generated yet.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="meeting-insights-card meeting-insights-card-full">
                <h3>Transcript</h3>
                {hasTranscript ? (
                  <div className="meeting-insights-empty">
                    Transcript viewing is served from the generated PDF report.
                    {canViewTranscript ? " Use View Transcript to open it in a new tab." : ""}
                  </div>
                ) : (
                  <div className="meeting-insights-empty">
                    {insights?.has_booking
                      ? insights?.meeting_completed
                        ? "Fetch the transcript to review the interview conversation."
                        : "The interview is not marked complete yet, so transcript generation is not available."
                      : "Transcript generation will be available after a confirmed meeting exists."}
                  </div>
                )}
              </div>

              <div className="meeting-insights-card meeting-insights-card-full">
                <h3>AI Chat</h3>
                {hasTranscript ? (
                  <>
                    <div className="meeting-insights-chat-form">
                      <textarea
                        value={question}
                        onChange={(event) => setQuestion(event.target.value)}
                        placeholder="Ask about candidate communication, technical depth, red flags, or any part of the interview transcript."
                      />
                      <div className="meeting-insights-chat-actions">
                        <div className="meeting-insights-muted">
                          AI answers are grounded in the saved transcript and summary.
                        </div>
                        <button
                          className="meeting-insights-btn meeting-insights-btn-primary"
                          onClick={handleAskQuestion}
                          disabled={actionLoading.chat || !question.trim()}
                        >
                          {actionLoading.chat ? "Thinking..." : "Ask AI"}
                        </button>
                      </div>
                    </div>

                    {chatHistory.length ? (
                      <div className="meeting-insights-chat-history">
                        {chatHistory.slice().reverse().map((item, index) => (
                          <div className="meeting-insights-chat-item" key={`${item.timestamp || "chat"}-${index}`}>
                            <h4>Question</h4>
                            <p>{item.question || "No question recorded."}</p>
                            <h4 style={{ marginTop: 12 }}>Answer</h4>
                            <p>{item.answer || "No answer recorded."}</p>
                            <div className="meeting-insights-chat-meta">
                              {item.timestamp ? formatDateLabel(item.timestamp) : "Timestamp unavailable"}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="meeting-insights-empty">
                        Ask the first question to start transcript-grounded AI chat for this interview.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="meeting-insights-empty">
                    AI chat becomes available once the transcript has been fetched successfully.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingInsightsModal;
